import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { apiRequest } from '../lib/api';
import type { Service, ServiceFormData } from '../types/services';

// =============================================
// Fetch Functions
// =============================================

async function fetchServices(
  accessToken: string | null | undefined,
  params?: { is_active?: boolean }
) {
  const searchParams = new URLSearchParams();
  if (params?.is_active !== undefined) {
    searchParams.append('is_active', String(params.is_active));
  }

  const queryString = searchParams.toString();
  const url = `/api/profile/services${queryString ? `?${queryString}` : ''}`;

  return apiRequest<{ services: Service[] }>(
    url,
    { method: 'GET' },
    accessToken
  );
}

async function fetchService(
  serviceId: string,
  accessToken: string | null | undefined
) {
  return apiRequest<Service>(
    `/api/profile/services/${serviceId}`,
    { method: 'GET' },
    accessToken
  );
}

// =============================================
// Hooks
// =============================================

export function useServices(params?: { is_active?: boolean }) {
  const { session } = useAuth();

  return useQuery<{ services: Service[] }>({
    queryKey: ['services', params],
    enabled: Boolean(session),
    queryFn: () => fetchServices(session?.access_token, params),
  });
}

export function useActiveServices() {
  return useServices({ is_active: true });
}

export function useService(serviceId: string) {
  const { session } = useAuth();

  return useQuery<Service>({
    queryKey: ['service', serviceId],
    enabled: Boolean(serviceId && session),
    queryFn: () => fetchService(serviceId, session?.access_token),
  });
}

export function useCreateService() {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ServiceFormData) => {
      return apiRequest<Service>(
        '/api/profile/services',
        {
          method: 'POST',
          body: JSON.stringify(data),
        },
        session?.access_token
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['services'] });
    },
  });
}

export function useUpdateService(serviceId: string) {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Partial<ServiceFormData>) => {
      return apiRequest<Service>(
        `/api/profile/services/${serviceId}`,
        {
          method: 'PATCH',
          body: JSON.stringify(data),
        },
        session?.access_token
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['services'] });
      queryClient.invalidateQueries({ queryKey: ['service', serviceId] });
    },
  });
}

export function useDeleteService() {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (serviceId: string) => {
      return apiRequest<{ success: boolean }>(
        `/api/profile/services/${serviceId}`,
        { method: 'DELETE' },
        session?.access_token
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['services'] });
    },
  });
}
